import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaRocket, FaEye, FaShieldAlt,
  FaArrowRight, FaCheckCircle, FaLightbulb, FaHandshake,
  FaAward, FaGlobe, FaCode, FaChartLine
} from 'react-icons/fa';
import './AboutUs.css';

const values = [
  {
    icon: <FaShieldAlt />,
    title: 'Security First',
    desc: 'Every solution is engineered with security at its foundation. We never compromise on protecting your financial data and systems.',
  },
  {
    icon: <FaLightbulb />,
    title: 'Innovation Driven',
    desc: 'We stay ahead of fintech trends, adopting cutting-edge technologies to deliver solutions that give our clients a competitive edge.',
  },
  {
    icon: <FaHandshake />,
    title: 'Client Partnership',
    desc: 'We treat every client as a long-term partner. Your success is our success, and we invest deeply in understanding your business.',
  },
  {
    icon: <FaAward />,
    title: 'Excellence & Quality',
    desc: 'We maintain the highest standards in code quality, security, and performance across every product we deliver.',
  },
  {
    icon: <FaGlobe />,
    title: 'Compliance & Ethics',
    desc: 'Operating with full regulatory compliance and ethical business practices is non-negotiable in everything we do.',
  },
  {
    icon: <FaRocket />,
    title: 'Agility & Speed',
    desc: 'We deliver fast without cutting corners. Our agile processes ensure rapid time-to-market with production-ready quality.',
  },
];

const milestones = [
  { year: '2006', event: 'Began professional career in software engineering — the start of an 18+ year technology journey across multiple domains.' },
  { year: '2010', event: 'Gained hands-on expertise in enterprise application development, system architecture, and large-scale software delivery.' },
  { year: '2014', event: 'Deepened specialization in financial technology — working on payment systems, transaction processing, and banking software.' },
  { year: '2018', event: 'Expanded into cloud architecture, API platforms, reconciliation engines, and fintech security frameworks.' },
  { year: '2022', event: 'Accumulated cross-domain experience spanning fintech, e-commerce, healthcare, logistics, and enterprise platforms.' },
  { year: '2020', event: 'Conceptualized Techmascot Technologies — a company built to bring deep technology experience to the fintech world.' },
  { year: '2021', event: 'Founded Techmascot Technologies Private Limited — officially launching our mission to build enterprise-grade fintech solutions.' },
];

const team = [
  {
    name: 'Founder & CEO',
    role: 'Founder & CEO',
    expertise: '18+ Years in Technology | Fintech, Cloud, Security & Enterprise Software',
    initials: 'TM',
  },
];

const expertise = [
  { icon: <FaCode />, area: 'Payment Systems', years: '10+ yrs' },
  { icon: <FaShieldAlt />, area: 'Fintech Security', years: '8+ yrs' },
  { icon: <FaChartLine />, area: 'Reconciliation', years: '7+ yrs' },
  { icon: <FaGlobe />, area: 'Cloud Architecture', years: '6+ yrs' },
];

function AboutUs() {
  return (
    <div className="about-page">
      <Helmet>
        <title>About Us | Techmascot Technologies Private Limited</title>
        <meta name="description" content="Learn about Techmascot Technologies — our mission, vision, core values, team and our journey as a leading fintech software development company." />
        <link rel="canonical" href="https://www.techmascot.com/about" />
        <meta property="og:title" content="About Us | Techmascot Technologies" />
        <meta property="og:description" content="Learn about Techmascot Technologies — our mission, vision, core values, team and our journey as a leading fintech software development company." />
        <meta property="og:url" content="https://www.techmascot.com/about" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Banner */}
      <section className="page-banner">
        <div className="banner-bg">
          <div className="banner-grid"></div>
        </div>
        <div className="container">
          <div className="banner-content">
            <span className="tag">Our Story</span>
            <h1>About <span>Techmascot</span></h1>
            <p>
              Founded in 2021, powered by 18+ years of technology expertise across fintech,
              cloud, security, and enterprise software development.
            </p>
          </div>
        </div>
        <div className="banner-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill="#FFFFFF" />
          </svg>
        </div>
      </section>

      {/* About Overview */}
      <section className="about-overview">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-text">
              <span className="tag">Who We Are</span>
              <h2 className="section-title">
                Engineering <span>Financial Technology</span> That Matters
              </h2>
              <p>
                Techmascot Technologies Private Limited was founded in 2021 by a technology professional
                with 18+ years of hands-on experience across diverse domains — fintech, banking, cloud
                infrastructure, enterprise software, e-commerce, and cybersecurity.
              </p>
              <p style={{ marginTop: '16px', color: 'var(--text-gray)' }}>
                We are a fresh company backed by deep, real-world expertise. Our focus is on building
                secure, scalable, and compliant fintech solutions — payment gateways, escrow platforms,
                reconciliation engines, and cloud-native financial applications — for banks, NBFCs,
                payment aggregators, and fintech startups.
              </p>
              <div className="overview-stats">
                {[
                  { val: '18+', label: 'Yrs Experience' },
                  { val: '2021', label: 'Founded' },
                  { val: '6+', label: 'Services' },
                  { val: '10+', label: 'Tech Domains' },
                ].map((s, i) => (
                  <div key={i} className="ov-stat">
                    <div className="ov-val">{s.val}</div>
                    <div className="ov-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="overview-visual">
              <div className="about-brand-card">
                <div className="brand-logo-large">T</div>
                <h3>Techmascot Technologies</h3>
                <p>Private Limited</p>
                <div className="brand-divider"></div>
                <div className="brand-tagline">
                  "Engineering Financial Technology That Matters"
                </div>
                <div className="brand-domain">www.techmascot.in</div>
                <div className="expertise-list">
                  {expertise.map((e, i) => (
                    <div key={i} className="expertise-row">
                      <span className="exp-icon">{e.icon}</span>
                      <span className="exp-area">{e.area}</span>
                      <span className="exp-years">{e.years}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mv-section">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card mission">
              <div className="mv-icon"><FaRocket /></div>
              <h3>Our Mission</h3>
              <p>
                To leverage 18+ years of real-world technology experience to build secure, scalable,
                and compliant fintech solutions that help financial institutions and businesses
                accelerate their digital transformation and deliver genuine value to their customers.
              </p>
              <ul className="mv-list">
                <li><FaCheckCircle /><span>Build reliable financial infrastructure</span></li>
                <li><FaCheckCircle /><span>Ensure regulatory compliance at scale</span></li>
                <li><FaCheckCircle /><span>Deliver measurable business outcomes</span></li>
              </ul>
            </div>
            <div className="mv-card vision">
              <div className="mv-icon vision-icon"><FaEye /></div>
              <h3>Our Vision</h3>
              <p>
                To grow Techmascot Technologies into India's most trusted fintech technology partner —
                a company known for engineering excellence, security-first thinking, and honest,
                expertise-backed delivery across all segments of financial services.
              </p>
              <ul className="mv-list">
                <li><FaCheckCircle /><span>Lead in fintech security innovation</span></li>
                <li><FaCheckCircle /><span>Expand to global fintech markets</span></li>
                <li><FaCheckCircle /><span>Shape the future of digital finance</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">What Drives Us</span>
            <h2 className="section-title">Our Core <span>Values</span></h2>
            <p className="section-subtitle">
              These principles guide every decision we make, every line of code we write,
              and every client relationship we build.
            </p>
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-icon">{v.icon}</div>
                <h4>{v.title}</h4>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Our Journey</span>
            <h2 className="section-title">The Journey <span>Behind Techmascot</span></h2>
          </div>
          <div className="timeline">
            {milestones.map((m, i) => (
              <div key={i} className={`timeline-item${i % 2 === 0 ? ' left' : ' right'}`}>
                <div className="timeline-content">
                  <div className="timeline-year">{m.year}</div>
                  <p>{m.event}</p>
                </div>
                <div className="timeline-dot"></div>
              </div>
            ))}
            <div className="timeline-line"></div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="team-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Leadership</span>
            <h2 className="section-title">The Person <span>Behind It</span></h2>
            <p className="section-subtitle">
              Techmascot Technologies is founded and led by a technology professional
              with 18+ years of experience building real-world software across multiple domains.
            </p>
          </div>
          <div className="team-grid">
            {team.map((member, i) => (
              <div key={i} className="team-card">
                <div className="team-avatar">{member.initials}</div>
                <h4>{member.name}</h4>
                <div className="team-role">{member.role}</div>
                <p>{member.expertise}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ind-cta">
        <div className="container">
          <div className="ind-cta-inner">
            <h2>Let's Build Together</h2>
            <p>
              Partner with Techmascot Technologies for your next fintech project.
              We bring the expertise, security, and commitment to make it a success.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary" style={{ background: '#FFFFFF', color: 'var(--primary)', borderColor: '#FFFFFF' }}>
                Contact Us <FaArrowRight />
              </Link>
              <Link to="/services" className="btn-secondary">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
